// ============================================================
// THE APPROACH - a group moving quietly as one.
//
// One of the party LEADS and rolls Silent Move for everybody. Each HELPER
// makes it easier, each BURDEN (the heavy servo-arm, the wounded man being
// carried) makes it harder. The result stands until the situation changes -
// and it is HIDDEN: the party knows who led, not how well it went.
//
// It is tested only when SOMEONE LOOKS: the sharpest of the opposition
// rolls Awareness against the degrees the approach was made with, and the
// answer is a proposal for the ladder like any other quiet move.
//
// The roles are public (on the frame's ladder record); the degrees are the
// GM's alone (the GM's desk).
//
// Pure. Zero React dependency.
// ============================================================
import type { CalledResult, TestCall } from '../narrative/callForTest';
import type { Outcome } from './proposals';

const APPROACH_ROLES = ['helper', 'burden'] as const;
export type ApproachRole = (typeof APPROACH_ROLES)[number];

export interface Approach {
  /** Who leads. Null until the GM names one. */
  leaderId: string | null;
  /** Everyone else with a part in it, by combatant id. Anyone absent simply follows. */
  roles: Record<string, ApproachRole>;
  /**
   * The leader has rolled for this company as it stands. Public - the party
   * knows an approach was made - while HOW WELL is the GM's alone. Any
   * change of who does what makes it untested again: the situation changed.
   */
  tested: boolean;
}

/** What each burden takes away. What each helper adds is the table's assist ruling (+10 as written). */
export const BURDEN_PENALTY = 10;
const HELPER_BONUS = 10;

export const emptyApproach = (): Approach => ({ leaderId: null, roles: {}, tested: false });

const isRole = (value: unknown): value is ApproachRole => APPROACH_ROLES.includes(value as ApproachRole);

/** A stored approach, validated. The leader never also holds a role. */
export function readApproach(raw: unknown): Approach {
  const stored = (raw ?? {}) as { leaderId?: unknown; roles?: unknown };
  const leaderId = typeof stored.leaderId === 'string' && stored.leaderId ? stored.leaderId : null;
  const entries = stored.roles && typeof stored.roles === 'object' ? Object.entries(stored.roles) : [];
  const roles = Object.fromEntries(
    entries.filter((e): e is [string, ApproachRole] => isRole(e[1]) && e[0] !== leaderId),
  );
  return { leaderId, roles, tested: leaderId !== null && (stored as { tested?: unknown }).tested === true };
}

/** Name the leader (or nobody). Whoever leads holds no other role. */
export function withLeader(approach: Approach, leaderId: string | null): Approach {
  const { [leaderId ?? '']: _was, ...roles } = approach.roles;
  return { leaderId, roles, tested: false };
}

/** The leader has rolled. Nothing to mark with nobody leading. */
export const withTested = (approach: Approach): Approach =>
  approach.leaderId ? { ...approach, tested: true } : approach;

/** Give somebody a part, or (null) take it away. The leader's part is leading. */
export function withRole(approach: Approach, id: string, role: ApproachRole | null): Approach {
  if (id === approach.leaderId) return approach;
  const { [id]: _was, ...rest } = approach.roles;
  return { ...approach, roles: role ? { ...rest, [id]: role } : rest, tested: false };
}

const count = (approach: Approach, role: ApproachRole) =>
  Object.values(approach.roles).filter((r) => r === role).length;

/** What the company does to the leader's test: the assist bonus a helper, -10 a burden. */
export function approachMod(approach: Approach, helperBonus = HELPER_BONUS): number {
  return count(approach, 'helper') * helperBonus - count(approach, 'burden') * BURDEN_PENALTY;
}

/** The part someone plays, in a word: leads, assists, a burden, or follows. */
export function partOf(approach: Approach, id: string): 'leads' | 'assists' | 'a burden' | 'follows' {
  if (id === approach.leaderId) return 'leads';
  const role = approach.roles[id];
  if (role === 'helper') return 'assists';
  return role === 'burden' ? 'a burden' : 'follows';
}

/**
 * The leader's test: Silent Move, alone and in secret, at the company's
 * modifier plus whatever the GM adds. Null with nobody leading.
 */
export function approachCall(approach: Approach, difficulty: number, helperBonus = HELPER_BONUS): TestCall | null {
  if (!approach.leaderId) return null;
  return {
    who: [approach.leaderId],
    subject: { kind: 'skill', id: 'silent_move' },
    difficulty: approachMod(approach, helperBonus) + difficulty,
    against: null,
    secret: true,
  };
}

/**
 * The degrees an approach was made with, as one signed number: degrees of
 * success, or minus the degrees of failure. Null when nothing was rolled.
 */
export function degreesOf(result: CalledResult | undefined): number | null {
  if (!result || result.passed === null) return null;
  return result.passed ? result.margin : -result.margin;
}

/** Somebody looking: Awareness, alone and in secret. */
export function lookCall(watcherId: string, difficulty: number): TestCall {
  return {
    who: [watcherId],
    subject: { kind: 'skill', id: 'awareness' },
    difficulty,
    against: null,
    secret: true,
  };
}

/**
 * Did the approach hold when somebody looked? The watcher notices only by
 * PASSING their test with more degrees than the approach was made with; an
 * approach that itself failed is beaten by any pass. The margin is how
 * clearly, either way. Null when the look was not rolled.
 */
export function lookOutcome(approachDegrees: number, look: CalledResult | undefined): Outcome | null {
  if (!look || look.passed === null) return null;
  const held = Math.max(0, approachDegrees);
  if (!look.passed) return { passed: true, margin: held + look.margin };
  const noticed = look.margin > held || approachDegrees < 0;
  return { passed: !noticed, margin: Math.abs(look.margin - held) + (approachDegrees < 0 ? -approachDegrees : 0) };
}
